<template>
  <div class="w-full">
    <!-- Staff Table -->
    <div class="bg-white rounded-lg shadow-sm p-4 w-full">
      <!-- Add Staff Button -->
      <div class="flex justify-end mb-4">
        <Button
          icon="pi pi-plus"
          label="Add Staff Member"
          severity="secondary"
          raised
          @click="openAddStaffDialog"
        />
      </div>

      <!-- Staff DataTable -->
      <DataTable
        :value="staffMembers"
        :loading="loading"
        class="w-full"
        striped-rows
      >
        <template #empty>
          <div class="text-center py-8">
            <i class="pi pi-users text-5xl text-gray-300 mb-3" />
            <p class="text-gray-500">
              No staff members found for this company
            </p>
          </div>
        </template>

        <Column
          field="contact.name"
          header="Name"
          sortable
        >
          <template #body="{ data }">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                <img
                  v-if="data.contact?.image_url"
                  :src="data.contact.image_url"
                  :alt="data.contact?.name"
                  class="w-full h-full object-cover"
                >
                <i
                  v-else
                  class="pi pi-user text-gray-600"
                />
              </div>
              <div>
                <div class="font-medium">
                  {{ data.contact?.name || 'Unknown' }}
                </div>
                <div class="text-sm text-gray-500">
                  {{ data.contact?.email || '' }}
                </div>
              </div>
            </div>
          </template>
        </Column>

        <Column
          field="position"
          header="Position"
          sortable
        >
          <template #body="{ data }">
            {{ data.position || '-' }}
          </template>
        </Column>

        <Column
          field="department"
          header="Department"
          sortable
        >
          <template #body="{ data }">
            {{ data.department || '-' }}
          </template>
        </Column>

        <Column
          field="contact.phone"
          header="Phone"
          sortable
        >
          <template #body="{ data }">
            {{ data.contact?.phone || '-' }}
          </template>
        </Column>

        <Column
          field="is_primary_contact"
          header="Primary"
          sortable
        >
          <template #body="{ data }">
            <Tag
              v-if="data.is_primary_contact"
              value="Primary"
              severity="success"
            />
            <span v-else>-</span>
          </template>
        </Column>

        <Column
          field="is_active"
          header="Status"
          sortable
        >
          <template #body="{ data }">
            <Tag
              :value="data.is_active ? 'Active' : 'Inactive'"
              :severity="data.is_active ? 'success' : 'secondary'"
            />
          </template>
        </Column>

        <Column
          header="Actions"
          style="width: 150px"
        >
          <template #body="{ data }">
            <div class="flex gap-2">
              <Button
                icon="pi pi-pencil"
                class="p-button-rounded p-button-text p-button-sm"
                @click="openEditStaffDialog(data)"
              />
              <Button
                icon="pi pi-trash"
                class="p-button-rounded p-button-text p-button-sm p-button-danger"
                @click="confirmDeleteStaff(data)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Add/Edit Staff Dialog -->
    <Dialog
      v-model:visible="isStaffDialogVisible"
      :header="staffDialogHeader"
      :style="{ width: '50vw' }"
      :modal="true"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="p-2 md:col-span-2">
          <label
            for="staff_contact_id"
            class="block text-gray-700 mb-2 font-medium"
          >Contact <span class="text-red-500">*</span></label>
          <Dropdown
            id="staff_contact_id"
            v-model="staffForm.contact_id"
            :options="contactsOptions"
            option-label="label"
            option-value="value"
            :disabled="staffDialogMode === 'edit'"
            placeholder="Select a contact"
            class="w-full"
            filter
            :filter-fields="['label']"
          />
          <small class="text-gray-500 mt-1 block">
            {{ staffDialogMode === 'edit' ? 'Contact cannot be changed' : 'Select an existing contact to add as staff member' }}
          </small>
        </div>

        <div class="p-2">
          <label
            for="staff_position"
            class="block text-gray-700 mb-2 font-medium"
          >Position</label>
          <InputText
            id="staff_position"
            v-model="staffForm.position"
            class="w-full"
            placeholder="e.g., Sales Manager"
          />
        </div>

        <div class="p-2 md:col-span-2">
          <label
            for="staff_department"
            class="block text-gray-700 mb-2 font-medium"
          >Department</label>
          <InputText
            id="staff_department"
            v-model="staffForm.department"
            class="w-full"
            placeholder="e.g., Sales"
          />
        </div>

        <div class="p-2 md:col-span-2">
          <div class="flex items-center">
            <Checkbox
              id="staff_is_primary_contact"
              v-model="staffForm.is_primary_contact"
              :binary="true"
            />
            <label
              for="staff_is_primary_contact"
              class="ml-2 text-gray-700"
            >Primary Contact</label>
          </div>
          <small class="text-gray-500 mt-1 block">
            Only one staff member can be the primary contact for this company
          </small>
        </div>

        <div class="p-2 md:col-span-2">
          <div class="flex items-center">
            <Checkbox
              id="staff_is_active"
              v-model="staffForm.is_active"
              :binary="true"
            />
            <label
              for="staff_is_active"
              class="ml-2 text-gray-700"
            >Active</label>
          </div>
        </div>

        <div class="p-2 md:col-span-2">
          <label
            for="staff_notes"
            class="block text-gray-700 mb-2 font-medium"
          >Notes</label>
          <Textarea
            id="staff_notes"
            v-model="staffForm.notes"
            rows="3"
            class="w-full"
            placeholder="Additional notes about this staff member"
          />
        </div>
      </div>

      <template #footer>
        <Button
          label="Cancel"
          class="p-button-text"
          :disabled="isSaving"
          @click="isStaffDialogVisible = false"
        />
        <Button
          :label="staffDialogMode === 'add' ? 'Add' : 'Update'"
          :disabled="isSaving"
          :loading="isSaving"
          @click="saveStaffMember"
        />
      </template>
    </Dialog>

    <Toast />
    <ConfirmDialog />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { useContactStore } from '@/stores/contactsStore';
import {
  getCompanyStaff,
  addStaffMember,
  updateStaffMember,
  deleteStaffMember
} from '@/services/companyStaffService';

const props = defineProps({
  company: {
    type: Object,
    required: true
  },
  isEditing: {
    type: Boolean,
    default: false
  }
});

const toast = useToast();
const confirm = useConfirm();
const contactStore = useContactStore();

// State
const staffMembers = ref([]);
const loading = ref(false);
const isStaffDialogVisible = ref(false);
const staffDialogMode = ref('add'); // 'add' or 'edit'
const staffDialogHeader = computed(() => {
  return staffDialogMode.value === 'add' ? 'Add Staff Member' : 'Edit Staff Member';
});
const isSaving = ref(false);
const editingStaffId = ref(null);

// Staff form
const staffForm = ref({
  contact_id: null,
  position: '',
  department: '',
  is_primary_contact: false,
  notes: '',
  is_active: true
});

// Contacts options for dropdown
const contactsOptions = computed(() => {
  // Get all contacts from the store
  const contacts = contactStore.contacts || [];
  
  // When editing, include the current contact in the list (for display)
  // When adding, filter out contacts that are already staff members
  if (staffDialogMode.value === 'edit' && staffForm.value.contact_id) {
    // Include all contacts (the current one will be selected but disabled)
    return contacts.map(contact => ({
      label: `${contact.name}${contact.email ? ` (${contact.email})` : ''}`,
      value: contact.id
    }));
  } else {
    // Filter out contacts that are already staff members for this company
    const existingStaffContactIds = staffMembers.value.map(staff => staff.contact_id);
    const availableContacts = contacts.filter(contact => !existingStaffContactIds.includes(contact.id));
    
    return availableContacts.map(contact => ({
      label: `${contact.name}${contact.email ? ` (${contact.email})` : ''}`,
      value: contact.id
    }));
  }
});

// Load staff members
const loadStaffMembers = async () => {
  if (!props.company?.id) return;
  
  loading.value = true;
  try {
    const response = await getCompanyStaff(props.company.id, { active: false }); // Get all staff (active and inactive)
    // Backend returns: { success: true, data: [...], current_page, per_page, total, last_page }
    staffMembers.value = response.data || response || [];
  } catch (error) {
    console.error('Error loading staff members:', error);
    toast.add({
      severity: 'error',
      summary: 'Load Failed',
      detail: 'Failed to load staff members',
      life: 5000
    });
    staffMembers.value = [];
  } finally {
    loading.value = false;
  }
};

// Load contacts if not already loaded
const loadContacts = async () => {
  if (contactStore.contacts.length === 0) {
    try {
      await contactStore.loadContacts();
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  }
};

// Open add staff dialog
const openAddStaffDialog = async () => {
  staffDialogMode.value = 'add';
  editingStaffId.value = null;
  staffForm.value = {
    contact_id: null,
    position: '',
    department: '',
    is_primary_contact: false,
    notes: '',
    is_active: true
  };
  
  // Load contacts if needed
  await loadContacts();
  
  isStaffDialogVisible.value = true;
};

// Open edit staff dialog
const openEditStaffDialog = (staff) => {
  staffDialogMode.value = 'edit';
  editingStaffId.value = staff.id;
  staffForm.value = {
    contact_id: staff.contact_id,
    position: staff.position || '',
    department: staff.department || '',
    is_primary_contact: staff.is_primary_contact || false,
    notes: staff.notes || '',
    is_active: staff.is_active !== undefined ? staff.is_active : true
  };
  isStaffDialogVisible.value = true;
};

// Save staff member
const saveStaffMember = async () => {
  if (isSaving.value) return;

  // Validation
  if (!staffForm.value.contact_id) {
    toast.add({
      severity: 'warn',
      summary: 'Validation Error',
      detail: 'Please select a contact',
      life: 5000
    });
    return;
  }

  isSaving.value = true;
  try {
    if (staffDialogMode.value === 'add') {
      // Add new staff member
      await addStaffMember(props.company.id, staffForm.value);
      toast.add({
        severity: 'success',
        summary: 'Staff Member Added',
        detail: 'Staff member added successfully!',
        life: 5000
      });
    } else {
      // Update existing staff member
      await updateStaffMember(props.company.id, editingStaffId.value, staffForm.value);
      toast.add({
        severity: 'success',
        summary: 'Staff Member Updated',
        detail: 'Staff member updated successfully!',
        life: 5000
      });
    }

    isStaffDialogVisible.value = false;
    await loadStaffMembers();
  } catch (error) {
    console.error('Error saving staff member:', error);
    const errorMessage = error.response?.data?.message || 
                        (error.response?.data?.errors ? 
                            Object.values(error.response.data.errors).flat().join(', ') : 
                            'Failed to save staff member');
    toast.add({
      severity: 'error',
      summary: 'Save Failed',
      detail: errorMessage,
      life: 7000
    });
  } finally {
    isSaving.value = false;
  }
};

// Confirm delete staff member
const confirmDeleteStaff = (staff) => {
  const contactName = staff.contact?.name || 'this staff member';
  confirm.require({
    message: `Are you sure you want to remove ${contactName} from this company? This action cannot be undone.`,
    header: 'Confirm Remove',
    icon: 'pi pi-exclamation-triangle',
    rejectClass: 'p-button-secondary p-button-outlined',
    rejectLabel: 'Cancel',
    acceptLabel: 'Remove',
    accept: async () => {
      try {
        await deleteStaffMember(props.company.id, staff.id);
        toast.add({
          severity: 'success',
          summary: 'Staff Member Removed',
          detail: 'Staff member removed successfully',
          life: 5000
        });
        await loadStaffMembers();
      } catch (error) {
        console.error('Error deleting staff member:', error);
        const errorMessage = error.response?.data?.message || 'Failed to remove staff member';
        toast.add({
          severity: 'error',
          summary: 'Remove Failed',
          detail: errorMessage,
          life: 7000
        });
      }
    }
  });
};

// Watch for company changes
watch(
  () => props.company?.id,
  (newCompanyId) => {
    if (newCompanyId) {
      loadStaffMembers();
    } else {
      staffMembers.value = [];
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (props.company?.id) {
    loadStaffMembers();
  }
  loadContacts();
});
</script>

<style scoped>
</style>
